import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CircularProgress, Link, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { ConfirmModal } from '~/components/ConfirmModal';
import { EmptyData } from '~/components/EmptyData';
import { Pagination } from '~/components/Pagination';
import { ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { Menu } from '~/pages/Companies/components/Menu';
import { useUpdateCompanyStatusMutation } from '~/services/rtkQuery/company';
import { GetInProgressCompaniesResponse } from '~/services/rtkQuery/company/types';
import { CompanyStatus } from '~/types/models';
import { formatUrl } from '~/utils/formats';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

const STATUSES: CompanyStatus[] = ['draft', 'closed', 'rejected'];

interface Props {
  fetchLimit: number;
  loading: boolean;
  currentPage: number;
  data: GetInProgressCompaniesResponse['result'] | null;
  onPageChange: (page: number) => void;
}

const Component: React.FC<Props> = ({ fetchLimit, loading, currentPage, data, onPageChange }) => {
  const navigate = useNavigate();
  const { showNotification, handleApiError } = useNotification();
  const [moreAnchorEl, setMoreAnchorEl] = React.useState<HTMLElement | null>(null);
  const [updateStatusConfirmModalVisible, setUpdateStatusConfirmModalVisible] = useState(false);
  const activeCompanyId = useRef<number | null>(null);
  const selectedNewStatus = useRef<CompanyStatus | null>(null);
  const [updateCompanyStatusMutation, { isLoading: isLoadingUpdateCompanyStatusMutation }] =
    useUpdateCompanyStatusMutation();

  const handleCompanyClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, id: number) => {
      // @ts-expect-error tagName exists
      if (event.target.tagName !== 'A') {
        navigate(generatePath(ROUTES.COMPANY, { id: id.toString() }));
      }
    },
    [navigate],
  );

  const showMoreMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, companyId: number) => {
      event.stopPropagation();

      activeCompanyId.current = companyId;
      setMoreAnchorEl(event.currentTarget);
    },
    [],
  );

  const hideMoreMenu = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
    event.stopPropagation();

    setMoreAnchorEl(null);
  }, []);

  const showUpdateStatusConfirmModal = useCallback(() => {
    setUpdateStatusConfirmModalVisible(true);
  }, []);

  const hideUpdateStatusConfirmModal = useCallback(() => {
    setUpdateStatusConfirmModalVisible(false);
  }, []);

  const handleStatusSelect = useCallback(
    (status: CompanyStatus) => {
      selectedNewStatus.current = status;

      showUpdateStatusConfirmModal();
    },
    [showUpdateStatusConfirmModal],
  );

  const handleUpdateStatusConfirm = useCallback(async () => {
    try {
      if (activeCompanyId.current && selectedNewStatus.current) {
        const response = await updateCompanyStatusMutation({
          status: selectedNewStatus.current,
          id: activeCompanyId.current,
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });

        hideUpdateStatusConfirmModal();
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [handleApiError, hideUpdateStatusConfirmModal, showNotification, updateCompanyStatusMutation]);

  const inProgressTotalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: data?.total || 0,
      fetchLimit,
    });
  }, [data?.total, fetchLimit]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (data?.data.length === 0) {
    return <EmptyData description="No companies available" title="In Progress" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles['container__table-container']}>
        <table className={styles.container__table}>
          <thead>
            <tr>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Company Name
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Decision Threshold
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Total Score
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Responsible
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Updated
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Website Link
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--black-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Status
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.data.map(company => (
              <tr key={company.id} onClick={event => handleCompanyClick(event, company.id)}>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.name}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.decision_threshold}%
                  </Typography>
                </td>
                <td>
                  <Typography
                    color={company.total_score > 50 ? 'var(--success-color)' : 'var(--error-color)'}
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.total_score}%
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {company.user ? `${company.user.fist_name} ${company.user.last_name}` : 'N/A'}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="700"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {format(new Date(company.updated_at), 'MMM dd, yyyy')}
                  </Typography>
                </td>
                <td>
                  <Link
                    color="rgb(var(--main-2-color-rgb))"
                    fontWeight="700"
                    href={formatUrl(company.website_url)}
                    lineHeight="20px"
                    target="_blank"
                    variant="body1"
                  >
                    {company.website_url}
                  </Link>
                </td>
                <td>
                  <div>
                    <div>
                      <Typography color="var(--success-color)" lineHeight="16px" variant="body1">
                        In Progress
                      </Typography>
                    </div>
                    <button
                      aria-label="More"
                      title="More"
                      type="button"
                      onClick={event => showMoreMenu(event, company.id)}
                    >
                      <MoreVertIcon
                        sx={{
                          color: 'var(--gray-6-color)',
                          '&:hover': { color: 'var(--gray-2-color)' },
                        }}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.container__footer}>
        <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
          {getCurrentPageInfo({
            currentPage,
            fetchLimit,
            currentPageItemsCount: data?.data.length || 0,
            totalItemsCount: data?.total || 0,
          })}
        </Typography>
        <Pagination
          currentPage={currentPage}
          totalPages={inProgressTotalPages}
          onChange={onPageChange}
        />
      </div>
      <Menu
        anchorEl={moreAnchorEl}
        statuses={STATUSES}
        onClose={hideMoreMenu}
        onSelect={handleStatusSelect}
      />
      <ConfirmModal
        description="Are you sure you want to update this company status?"
        loading={isLoadingUpdateCompanyStatusMutation}
        open={updateStatusConfirmModalVisible}
        title="Do you want to update the status?"
        onCancel={hideUpdateStatusConfirmModal}
        onConfirm={handleUpdateStatusConfirm}
      />
    </div>
  );
};

export const DealsInProgress = memo(Component);
